import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Skeleton } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { ParsedAttachmentRef } from '../../attachment-markers';
import { ChatAttachmentService } from 'src/app/services/chat-attachment.service';

interface RenderedAttachment {
  id: string;
  filename: string;
  kind: 'image' | 'pdf' | 'document';
  loading: boolean;
  error: boolean;
  objectUrl?: string;
}

@Component({
  selector: 'app-message-attachments',
  imports: [CommonModule, ButtonModule, DialogModule, Skeleton, TooltipModule],
  templateUrl: './message-attachments.component.html',
})
export class MessageAttachmentsComponent implements OnChanges {
  @Input() attachments: ParsedAttachmentRef[] = [];
  /** Show the per-attachment "Scan this document" action. Off in read-only/admin views. */
  @Input() allowScan = false;
  @Output() scanRequested = new EventEmitter<{ id: string; filename: string }>();

  private readonly chatAttachmentService = inject(ChatAttachmentService);
  private loadVersion = 0;

  items: RenderedAttachment[] = [];
  imageDialogVisible = false;
  selectedImageUrl: string | null = null;
  selectedImageFilename = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attachments']) {
      void this.loadAttachments();
    }
  }

  async loadAttachments(): Promise<void> {
    const version = ++this.loadVersion;

    this.items = this.attachments.map((attachment) => ({
      id: attachment.id,
      filename: attachment.filename,
      kind: this.getAttachmentKind(attachment.filename),
      loading: true,
      error: false,
    }));

    await Promise.all(
      this.items.map(async (item, index) => {
        try {
          const objectUrl = await this.chatAttachmentService.getObjectUrl(
            item.id
          );
          if (version !== this.loadVersion) return;
          this.items[index] = { ...item, objectUrl, loading: false };
        } catch {
          if (version !== this.loadVersion) return;
          this.items[index] = { ...item, loading: false, error: true };
        }
      })
    );
  }

  onAttachmentClick(item: RenderedAttachment): void {
    if (item.loading || item.error || !item.objectUrl) return;

    if (item.kind !== 'image') {
      this.downloadAttachment(item);
      return;
    }

    this.selectedImageUrl = item.objectUrl;
    this.selectedImageFilename = item.filename;
    this.imageDialogVisible = true;
  }

  onScanClick(item: RenderedAttachment): void {
    if (item.loading || item.error || item.kind === 'document') return;
    this.scanRequested.emit({ id: item.id, filename: item.filename });
  }

  private downloadAttachment(item: RenderedAttachment): void {
    if (!item.objectUrl) return;

    const link = document.createElement('a');
    link.href = item.objectUrl;
    link.download = item.filename || 'attachment';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private getAttachmentKind(filename: string): RenderedAttachment['kind'] {
    if (filename.toLowerCase().endsWith('.pdf')) return 'pdf';
    if (/\.(docx|xlsx|csv|txt|md|pptx)$/i.test(filename)) return 'document';
    // Older clipboard images may have been stored without a file extension.
    return 'image';
  }
}
